import React, {useContext, useEffect, useMemo, useState} from 'react';
import {
    Box,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import ZeiterfassungDashboardContext from '../../../../../../Contexts/ZeiterfassungDashboardContext/ZeiterfassungDashboardContext';
import {ArbeitszeitService} from '../../../../../../services/openapi/services/ArbeitszeitService';
import {BauvorhabenWithID} from '../../../../../../services/openapi';
import APIWrapperContext from '../../../../../../services/APIWrapperContext';
import WorktimeRow from './WorktimeRow';
import DateHeader from './DateHeader';
import {IRowData, IWorkingDataOverviewTableProps} from './types';
import {convertDateStringToGermanString, convertDateToGermanString, convertGermanStringToDate} from './utils';

const WorkingDataOverviewTable: React.FC<IWorkingDataOverviewTableProps> = (props) => {
    const {data, user_zeiterfassung, timeRange, notes, isAdmin = false, bauvorhabenList: bauvorhabenListProp} = props;
    const {isLoading} = useContext(ZeiterfassungDashboardContext);
    const {call_backend_api} = useContext(APIWrapperContext);
    const [bauvorhabenListLocal, setBauvorhabenListLocal] = useState<BauvorhabenWithID[]>([]);

    useEffect(() => {
        if (isAdmin && !bauvorhabenListProp) {
            call_backend_api(
                ArbeitszeitService.getAlleBauvorhabenArbeitzeitBauvorhabenGet(),
                (response: BauvorhabenWithID[]) => {
                    setBauvorhabenListLocal(response);
                },
                (error: any) => {
                    console.error('Error loading Bauvorhaben:', error);
                },
                undefined,
                false
            );
        }
    }, [isAdmin, bauvorhabenListProp, call_backend_api]);

    const bauvorhabenList = bauvorhabenListProp || bauvorhabenListLocal;

    const userMap = useMemo(() => {
        const map = new Map();
        user_zeiterfassung.forEach((user) => {
            map.set(user.user_id, user);
        });
        return map;
    }, [user_zeiterfassung]);

    const notesMap = useMemo(() => {
        const map = new Map();
        if (!notes) return map;

        notes.forEach((note) => {
            if (!note || !note.datum) return;

            const key = `${note.user_id}_${convertDateStringToGermanString(note.datum)}`;
            if (!map.has(key)) {
                map.set(key, []);
            }
            map.get(key).push(note);
        });
        return map;
    }, [notes]);

    const rows = useMemo(() => {
        const dateMap = new Map();
        let currentDate = new Date(timeRange.start);

        while (currentDate <= timeRange.end) {
            const dateStr = convertDateToGermanString(currentDate);
            dateMap.set(dateStr, new Map());

            user_zeiterfassung.forEach((user) => {
                const userData = {
                    gesamt_arbeitszeit: 0,
                    gesamt_pause: 0,
                    arbeitszeit_details: [],
                    BEARBEITER: user.fullname
                        ? `${user.fullname} (${user.NameAbbreviation || '---'})`
                        : `${user.email} (${user.NameAbbreviation || '---'})`,
                    notes: notesMap.get(`${user.user_id}_${dateStr}`) || [],
                };
                dateMap.get(dateStr).set(user.user_id, userData);
            });
            currentDate.setDate(currentDate.getDate() + 1);
        }

        if (data && data.length > 0) {
            data.forEach((entry) => {
                if (!dateMap.has(entry.DATUM)) {
                    dateMap.set(entry.DATUM, new Map());
                }

                if (!dateMap.get(entry.DATUM).has(entry.user_id)) {
                    const user = userMap.get(entry.user_id);
                    const userData = {
                        gesamt_arbeitszeit: 0,
                        gesamt_pause: 0,
                        arbeitszeit_details: [],
                        BEARBEITER: user
                            ? user.fullname
                                ? `${user.fullname} (${user.NameAbbreviation || '---'})`
                                : `${user.email} (${user.NameAbbreviation || '---'})`
                            : `${entry.user_id} ${entry.DATUM}`,
                        notes: notesMap.get(`${entry.user_id}_${entry.DATUM}`) || [],
                    };
                    dateMap.get(entry.DATUM).set(entry.user_id, userData);
                }

                const userData = dateMap.get(entry.DATUM).get(entry.user_id);
                userData.gesamt_arbeitszeit += entry.DAUER;
                userData.gesamt_pause += entry.PAUSE;
                userData.arbeitszeit_details.push(entry);
            });
        }

        const rowsData: IRowData[] = [];

        const sortedDates = Array.from(dateMap.keys()).sort((a, b) => {
            return convertGermanStringToDate(a).getTime() - convertGermanStringToDate(b).getTime();
        });

        sortedDates.forEach((dateStr) => {
            const dateData = dateMap.get(dateStr);

            dateData.forEach((userData: any, userId: string) => {
                rowsData.push({
                    DATUM: dateStr,
                    gesamt_arbeitszeit: userData.gesamt_arbeitszeit,
                    gesamt_pause: userData.gesamt_pause,
                    arbeitszeit_details: userData.arbeitszeit_details,
                    user_id: userId,
                    BEARBEITER: userData.BEARBEITER,
                    arbeitszeit_erfuellt: userData.gesamt_arbeitszeit >= 8.5,
                    note: userData.notes,
                });
            });
        });

        return rowsData;
    }, [data, user_zeiterfassung, timeRange, userMap, notesMap]);

    const groupedRows = useMemo(() => {
        const result = new Map<string, IRowData[]>();

        rows.forEach((row) => {
            if (!result.has(row.DATUM)) {
                result.set(row.DATUM, []);
            }
            result.get(row.DATUM)!.push(row);
        });

        return result;
    }, [rows]);

    const sortedDates = useMemo(() => {
        return Array.from(groupedRows.keys()).sort((a, b) => {
            return convertGermanStringToDate(a).getTime() - convertGermanStringToDate(b).getTime();
        });
    }, [groupedRows]);

    if (isLoading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', padding: 4}}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <TableContainer
            component={Paper}
            sx={{
                maxHeight: '60vh',
                borderRadius: '12px',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
                overflowY: 'auto',
                overflowX: 'auto',
            }}>
            <Table stickyHeader aria-label='collapsible table'>
                <TableHead>
                    <TableRow>
                        <TableCell
                            sx={{
                                backgroundColor: 'primary.main',
                                color: 'white',
                                '& .MuiTypography-root': {color: 'white'},
                            }}
                        />
                        <TableCell
                            align='left'
                            sx={{
                                backgroundColor: 'primary.main',
                                color: 'white',
                                '& .MuiTypography-root': {color: 'white'},
                            }}>
                            <Typography variant='subtitle2' fontWeight={600}>
                                Mitarbeiter/-in
                            </Typography>
                        </TableCell>
                        <TableCell
                            align='right'
                            sx={{
                                backgroundColor: 'primary.main',
                                color: 'white',
                                '& .MuiTypography-root': {color: 'white'},
                            }}>
                            <Typography variant='subtitle2' fontWeight={600}>
                                Arbeitszeit (h)
                            </Typography>
                        </TableCell>
                        <TableCell
                            align='right'
                            sx={{
                                backgroundColor: 'primary.main',
                                color: 'white',
                                '& .MuiTypography-root': {color: 'white'},
                            }}>
                            <Typography variant='subtitle2' fontWeight={600}>
                                Pausenzeit (h)
                            </Typography>
                        </TableCell>
                        <TableCell
                            align='right'
                            sx={{
                                backgroundColor: 'primary.main',
                                color: 'white',
                                '& .MuiTypography-root': {color: 'white'},
                            }}>
                            <Typography variant='subtitle2' fontWeight={600}>
                                Arbeitszeit erfüllt
                            </Typography>
                        </TableCell>
                        <TableCell
                            align='right'
                            sx={{
                                backgroundColor: 'primary.main',
                                color: 'white',
                                '& .MuiTypography-root': {color: 'white'},
                            }}>
                            <Typography variant='subtitle2' fontWeight={600}>
                                Notiz
                            </Typography>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sortedDates.map((date) => (
                        <React.Fragment key={`date-group-${date}`}>
                            <DateHeader dateStr={date} />
                            {groupedRows.get(date)!.map((row) => (
                                <WorktimeRow
                                    key={`row-${row.DATUM}-${row.user_id}`}
                                    row={row}
                                    isAdmin={isAdmin}
                                    bauvorhabenList={bauvorhabenList}
                                />
                            ))}
                        </React.Fragment>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default React.memo(WorkingDataOverviewTable);
